import { apiService } from "./apiService";
import { OnboardingData } from "../types/onboarding";
import { ContactsService } from "./contactsService";

// Define the data shape for account info
export interface AccountInfo {
  firstName: string;
  lastName: string;
  email: string;
}

const onboardingService = {
  /**
   * Save or update user profile info in the database.
   */
  async saveAccountInfo(userId: string, data: AccountInfo): Promise<boolean> {
    console.log("Saving account info for user:", userId, data);
    await apiService.post("/user/onboarding/account-info", {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
    });
    return true;
  },

  /**
   * Save or update company info for the user.
   */
  async saveCompanyInfo(
    userId: string,
    companyName: string,
    companyWebsite: string
  ): Promise<boolean> {
    // First, check if a company already exists for this user
    await apiService.post("/user/onboarding/company", {
      companyName,
      companyWebsite,
    });
    return true;
  },

  /**
   * Update the onboarding JSONB column in the users table.
   */
  async updateOnboarding(
    userId: string,
    onboardingData: OnboardingData
  ): Promise<boolean> {
    await apiService.post("/user/onboarding/update", {
      onboarding: onboardingData,
    });
    return true;
  },

  /**
   * Mark the user's onboarding as complete in auth metadata.
   */
  async completeOnboarding(
    userId: string,
    onboardingData: OnboardingData
  ): Promise<boolean> {
    // Save selected contacts if any
    if (
      onboardingData.selectedContacts &&
      onboardingData.selectedContacts.length > 0
    ) {
      try {
        await ContactsService.saveSelectedContacts(
          onboardingData.selectedContacts
        );
      } catch (error) {
        console.error("Error saving selected contacts:", error);
        // Don't fail the entire onboarding if contact saving fails
      }
    }

    // Update Supabase Auth metadata
    const updatedOnboardingData = {
      ...onboardingData,
      onboarding_complete: true,
    };
    await apiService.completeOnboarding({ onboarding: updatedOnboardingData });
    return true;
  },

  /**
   * Complete portfolio company onboarding and create VC-managed workflow.
   * Called when a portfolio company (invited by VC) completes onboarding.
   */
  async completePortfolioOnboarding(
    userId: string,
    onboardingData: OnboardingData
  ): Promise<{ success: boolean; workflow?: any; error?: string }> {
    // Save selected contacts if any
    if (
      onboardingData.selectedContacts &&
      onboardingData.selectedContacts.length > 0
    ) {
      try {
        await ContactsService.saveSelectedContacts(
          onboardingData.selectedContacts
        );
      } catch (error) {
        console.error("Error saving selected contacts:", error);
        // Don't fail the entire onboarding if contact saving fails
      }
    }

    try {
      // Call the portfolio-specific completion endpoint
      // This will verify required integrations and create the VC-managed workflow
      const response = await apiService.post("/user/onboarding/complete-portfolio", {
        onboarding: {
          ...onboardingData,
          onboarding_complete: true,
        },
      });

      return {
        success: true,
        workflow: response?.data?.workflow,
      };
    } catch (error: any) {
      console.error("Error completing portfolio onboarding:", error);
      return {
        success: false,
        error: error?.response?.data?.error || error?.message || "Failed to complete portfolio onboarding",
      };
    }
  },
};

export default onboardingService;
